import { STORE_WHATSAPP_E164 } from "../../api"
import { readOrderToken } from "./orderToken"

/** "Festa de Outubro 2026!" -> "festa-de-outubro-2026" */
export const slugify = (text: string): string =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

export const getEventSlug = (eventName: string): string => slugify(eventName)

/** Eventos do organizador cujo nome gera o slug informado (pode haver mais de um). */
export const findEventsBySlug = <T extends { name: string }>(
  events: T[],
  slug: string,
): T[] => {
  const target = slugify(slug)
  if (!target) return []
  return events.filter((e) => getEventSlug(e.name) === target)
}

/** Query param do link público com o token do pedido (só abre aquele pedido). */
export const ORDER_TOKEN_PARAM = "k"

/**
 * Link público dos ingressos: EVENTO.listapix.com.br/slug-do-evento/ingressos/ID?k=TOKEN.
 * O subdomínio já é o do organizador, então basta a origem atual. Sem token
 * (pedido de outro aparelho), a página usa a sessão de quem está logado.
 */
export const buildTicketsLink = (
  eventName: string,
  orderId: string,
  orderToken?: string,
): string => {
  const base = `${window.location.origin}/${getEventSlug(eventName)}/ingressos/${encodeURIComponent(orderId)}`

  return orderToken
    ? `${base}?${ORDER_TOKEN_PARAM}=${encodeURIComponent(orderToken)}`
    : base
}

export type TPublicAccess =
  | { kind: "token"; token: string; eventId: string }
  | { kind: "session" }
  | { kind: "login" }
  | { kind: "denied" }

/**
 * Como a página pública lê o pedido: token do link (só se for deste pedido),
 * sessão do aparelho ou tela de login. Link antigo (?t=) cai no login.
 */
export const resolvePublicAccess = ({
  linkToken,
  orderId,
  hasSession,
}: {
  linkToken: string | null
  orderId: string
  hasSession: boolean
}): TPublicAccess => {
  if (linkToken) {
    const claims = readOrderToken(linkToken)
    if (!claims || claims.orderId !== orderId) return { kind: "denied" }
    return { kind: "token", token: linkToken, eventId: claims.eventId }
  }

  return hasSession ? { kind: "session" } : { kind: "login" }
}

/** Código curto do pedido, o mesmo exibido no card de "Meus ingressos". */
export const getOrderPublicCode = (extref?: string | null): string => {
  const ref = extref ? String(extref) : ""
  if (!ref) return ""
  return (ref.length > 6 ? ref.slice(-6) : ref).toUpperCase()
}

export const buildRequestTicketsUrl = (publicCode: string): string => {
  const text = `Quero receber meus ingressos aqui no whatsapp. Pedido #${publicCode}`
  return `https://wa.me/${STORE_WHATSAPP_E164}?text=${encodeURIComponent(text)}`
}

export const buildSaveTicketsUrl = (params: {
  phone: string
  quantity: number
  eventName: string
  eventDate?: string
  link: string
}): string => {
  const digits = params.phone.replace(/\D/g, "")
  const e164 = digits.startsWith("55") && digits.length >= 12 ? digits : `55${digits}`

  const single = params.quantity === 1
  const eventLine = params.eventDate
    ? `${params.eventName} | ${params.eventDate}`
    : params.eventName

  const text = `🎟️ SEU INGRESSO CHEGOU!

🎉 ${eventLine}
✅ ${params.quantity} ${single ? "ingresso confirmado" : "ingressos confirmados"}!

👇 Abra ${single ? "seu ingresso" : "seus ingressos"} aqui:
${params.link}

💾 Ao abrir, você também pode salvar em PDF para acessar sem internet. Guarde esta mensagem para encontrar ${single ? "seu ingresso" : "seus ingressos"} no dia do evento!`

  return `https://api.whatsapp.com/send?phone=${e164}&text=${encodeURIComponent(text)}`
}
